import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Button,
  Card,
  DatePicker,
  Descriptions,
  Drawer,
  Empty,
  Image,
  Input,
  Modal,
  Pagination,
  Select,
  Space,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd'
import { DownloadOutlined, EyeOutlined, ReloadOutlined } from '@ant-design/icons'
import axios from 'axios'
import dayjs from 'dayjs'
import type { Dayjs } from 'dayjs'
import { appApiUrls } from '@shared/url-map.js'
import type { ScanImageAdminItem, ScanImageAdminListResponse } from '@shared/common-types.js'

const { RangePicker } = DatePicker
const { Paragraph, Text } = Typography

type ScanStatusFilter = 'all' | 'pending' | 'processing' | 'success' | 'failed'
type CategoryFilter = 'all' | 'plant' | 'animal' | 'insect' | 'object'
type DateRangeValue = [Dayjs | null, Dayjs | null] | null

const STATUS_META: Record<ScanStatusFilter, { label: string; color: string }> = {
  all: { label: '全部', color: 'default' },
  pending: { label: '待上传', color: 'default' },
  processing: { label: '处理中', color: 'blue' },
  success: { label: '已完成', color: 'green' },
  failed: { label: '失败', color: 'red' },
}

const CATEGORY_OPTIONS: { value: CategoryFilter; label: string }[] = [
  { value: 'all', label: '全部类别' },
  { value: 'plant', label: '植物' },
  { value: 'animal', label: '动物' },
  { value: 'insect', label: '昆虫' },
  { value: 'object', label: '物品' },
]

export default function ScanImagesPage() {
  const [records, setRecords] = useState<ScanImageAdminItem[]>([])
  const [loading, setLoading] = useState(false)
  const [statusFilter, setStatusFilter] = useState<ScanStatusFilter>('all')
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all')
  const [dateRange, setDateRange] = useState<DateRangeValue>(null)
  const [keyword, setKeyword] = useState('')
  const [keywordInput, setKeywordInput] = useState('')
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 12,
    total: 0,
    totalPages: 0,
  })
  const [detailRecord, setDetailRecord] = useState<ScanImageAdminItem | null>(null)
  const [textPreview, setTextPreview] = useState<{ title: string; content: string } | null>(null)

  const fetchScanImages = useCallback(
    async (
      page = 1,
      pageSize = pagination.pageSize,
      overrides?: {
        keyword?: string
        status?: ScanStatusFilter
        range?: DateRangeValue
        category?: CategoryFilter
      },
    ) => {
      setLoading(true)
      try {
        const effectiveKeyword = overrides?.keyword ?? keyword
        const effectiveStatus = overrides?.status ?? statusFilter
        const effectiveRange = overrides?.range ?? dateRange
        const effectiveCategory = overrides?.category ?? categoryFilter

        const params: Record<string, string | number> = {
          page,
          pageSize,
        }

        if (effectiveStatus !== 'all') {
          params.status = effectiveStatus
        }

        if (effectiveKeyword) {
          params.keyword = effectiveKeyword
        }

        if (effectiveCategory !== 'all') {
          params.category = effectiveCategory
        }

        if (effectiveRange?.[0] && effectiveRange?.[1]) {
          params.dateFrom = effectiveRange[0].startOf('day').toISOString()
          params.dateTo = effectiveRange[1].endOf('day').toISOString()
        }

        const response = await axios.get<{ code: number; data: ScanImageAdminListResponse | null }>(
          appApiUrls.scanImage.list,
          { params },
        )

        if (!response.data?.data) {
          throw new Error('响应数据为空')
        }

        const payload = response.data.data
        setRecords(payload.list)
        setPagination(payload.pagination)
      } catch (error) {
        console.error('获取识别任务失败:', error)
        message.error('获取识别任务失败，请稍后重试')
      } finally {
        setLoading(false)
      }
    },
    [categoryFilter, dateRange, keyword, pagination.pageSize, statusFilter],
  )

  useEffect(() => {
    fetchScanImages(1, pagination.pageSize)
  }, [fetchScanImages, pagination.pageSize])

  const statusOptions = useMemo(
    () =>
      (Object.keys(STATUS_META) as ScanStatusFilter[]).map((value) => ({
        label: STATUS_META[value].label,
        value,
      })),
    [],
  )

  const onSearch = (value: string) => {
    const trimmed = value.trim()
    setKeyword(trimmed)
    setKeywordInput(trimmed)
    fetchScanImages(1, pagination.pageSize, { keyword: trimmed })
  }

  const handleDateChange = (value: DateRangeValue) => {
    if (value && value[0] && value[1]) {
      setDateRange([value[0], value[1]])
    } else {
      setDateRange(null)
    }
  }

  const handleStatusChange = (value: ScanStatusFilter) => {
    setStatusFilter(value)
    fetchScanImages(1, pagination.pageSize, { status: value })
  }

  const handleCategoryChange = (value: CategoryFilter) => {
    setCategoryFilter(value)
    fetchScanImages(1, pagination.pageSize, { category: value })
  }

  const handleTextClick = useCallback(
    (event: React.MouseEvent<HTMLElement>, title: string, content?: string | null) => {
      const target = event.target as HTMLElement
      if (!content || target.closest('.ant-typography-copy')) {
        return
      }
      setTextPreview({ title, content })
    },
    [],
  )

  const handlePaginationChange = (page: number, pageSize?: number) => {
    fetchScanImages(page, pageSize ?? pagination.pageSize)
  }

  return (
    <div className="space-y-6 p-6">
      <Card>
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <Space wrap>
            <Select value={statusFilter} options={statusOptions} style={{ width: 150 }} onChange={handleStatusChange} />
            <Select
              value={categoryFilter}
              options={CATEGORY_OPTIONS}
              style={{ width: 150 }}
              onChange={handleCategoryChange}
            />
            <RangePicker
              allowClear
              value={dateRange ?? undefined}
              onChange={(value) => handleDateChange((value as DateRangeValue) ?? null)}
            />
            <Input.Search
              placeholder="搜索 ID / Token / IP / 名称"
              allowClear
              value={keywordInput}
              onChange={(e) => setKeywordInput(e.target.value)}
              onSearch={onSearch}
              style={{ width: 280 }}
            />
          </Space>
          <Button
            icon={<ReloadOutlined />}
            loading={loading}
            onClick={() => fetchScanImages(pagination.current, pagination.pageSize, { keyword })}
          >
            刷新
          </Button>
        </div>
      </Card>

      <Card>
        {records.length === 0 && !loading ? (
          <Empty description="暂无识别任务" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {records.map((record) => {
              const statusMeta =
                STATUS_META[(record.statusLabel as ScanStatusFilter) || 'pending'] ?? STATUS_META.pending
              const confidenceLabel =
                typeof record.confidence === 'number' ? `${Math.round(record.confidence * 100)}%` : '—'

              return (
                <div
                  key={record.id}
                  className="flex h-[520px] flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm transition-shadow hover:shadow-md"
                >
                  <div className="relative h-48 w-full bg-gray-50">
                    {record.imageUrl ? (
                      <Image
                        src={record.imageUrl}
                        alt={record.commonName ?? record.id}
                        className="h-full w-full object-cover"
                        height="100%"
                        width="100%"
                        preview={{
                          maskClassName: 'flex items-center justify-center',
                          mask: (
                            <div className="flex items-center gap-2">
                              <EyeOutlined />
                              预览
                            </div>
                          ),
                        }}
                      />
                    ) : (
                      <div className="flex h-full items-center justify-center text-sm text-gray-400">暂无图片</div>
                    )}
                    <Tag
                      color={statusMeta.color === 'default' ? undefined : statusMeta.color}
                      className="absolute left-3 top-3 rounded-full px-3 py-1 text-xs"
                    >
                      {statusMeta.label}
                    </Tag>
                  </div>

                  <div className="flex flex-1 flex-col gap-3 p-4">
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>{dayjs(record.createdAt).format('MM-DD HH:mm')}</span>
                      <span>{record.modelDuration ? `${record.modelDuration.toLocaleString()} ms` : '—'}</span>
                    </div>
                    <div>
                      <Text className="text-base font-semibold text-gray-900">{record.commonName || '未识别名称'}</Text>
                      <div className="mt-1 flex items-center gap-2 text-xs text-gray-500">
                        <Tag color="cyan">{record.category || '未知类别'}</Tag>
                        <Tag color="gold">置信度 {confidenceLabel}</Tag>
                      </div>
                    </div>
                    {record.description && (
                      <Paragraph
                        className="mb-0 cursor-pointer text-sm text-gray-600"
                        ellipsis={{ rows: 2 }}
                        style={{ whiteSpace: 'pre-wrap' }}
                        onClick={(event) => handleTextClick(event, 'Description', record.description)}
                      >
                        {record.description}
                      </Paragraph>
                    )}
                    {record.funFact && (
                      <Paragraph
                        className="mb-0 text-xs text-gray-500"
                        ellipsis={{ rows: 2 }}
                        style={{ whiteSpace: 'pre-wrap' }}
                        onClick={(event) => handleTextClick(event, 'Fun Fact', record.funFact)}
                      >
                        {record.funFact}
                      </Paragraph>
                    )}
                    <div className="rounded-lg bg-gray-50 p-3 text-xs text-gray-500">
                      <div className="font-medium text-gray-800">{record.userToken || '未留Token'}</div>
                      <div>{record.userIp || '未知IP'}</div>
                      <div>Locale: {record.localizationCode || '--'}</div>
                      <div>
                        {(record.appInfo?.appVersion || 'Unknown') + ' · ' + (record.appInfo?.osVersion || 'Unknown')}
                      </div>
                    </div>
                    <div className="flex flex-1 flex-col gap-2 text-xs text-gray-600">
                      <div>
                        <Text type="secondary">Prompt</Text>
                        <Paragraph
                          className="mb-0 cursor-pointer text-xs"
                          ellipsis={{ rows: 2 }}
                          style={{ whiteSpace: 'pre-wrap' }}
                          onClick={(event) => handleTextClick(event, 'Prompt', record.prompt)}
                        >
                          {record.prompt}
                        </Paragraph>
                      </div>
                      {record.responseContent && (
                        <div>
                          <Text type="secondary">AI 响应</Text>
                          <Paragraph
                            className="mb-0 cursor-pointer text-xs"
                            ellipsis={{ rows: 2 }}
                            style={{ whiteSpace: 'pre-wrap' }}
                            onClick={(event) => handleTextClick(event, 'AI 响应', record.responseContent)}
                          >
                            {record.responseContent}
                          </Paragraph>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-between border-t border-gray-100 px-4 py-3 text-xs text-gray-500">
                    <div>ID: {record.id}</div>
                    <Space>
                      <Tooltip title="查看详情">
                        <Button size="small" onClick={() => setDetailRecord(record)}>
                          详情
                        </Button>
                      </Tooltip>
                      {record.imageUrl && (
                        <Tooltip title="下载图片">
                          <Button
                            size="small"
                            type="default"
                            icon={<DownloadOutlined />}
                            href={appApiUrls.scanImage.file(record.id)}
                            target="_blank"
                          >
                            下载
                          </Button>
                        </Tooltip>
                      )}
                    </Space>
                  </div>
                </div>
              )
            })}
          </div>
        )}

        <div className="mt-6 flex justify-end">
          <Pagination
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            showSizeChanger
            showTotal={(total) => `共 ${total} 条`}
            onChange={handlePaginationChange}
            onShowSizeChange={handlePaginationChange}
          />
        </div>
      </Card>

      {detailRecord && (
        <Drawer key={detailRecord.id} title="识别详情" open width={760} onClose={() => setDetailRecord(null)}>
          <div className="space-y-4">
            {detailRecord.imageUrl ? (
              <Image
                src={detailRecord.imageUrl}
                alt={detailRecord.id}
                className="max-h-96 w-full rounded-md object-cover"
              />
            ) : (
              <Tag color="warning">当前记录没有图片</Tag>
            )}
            <Descriptions column={2} bordered size="small">
              <Descriptions.Item label="状态" span={1}>
                <Tag color={STATUS_META[(detailRecord.statusLabel as ScanStatusFilter) || 'pending']?.color}>
                  {STATUS_META[(detailRecord.statusLabel as ScanStatusFilter) || 'pending']?.label}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="模型">
                {detailRecord.modelName} · {detailRecord.modelDuration || '--'} ms
              </Descriptions.Item>
              <Descriptions.Item label="类别">{detailRecord.category || '--'}</Descriptions.Item>
              <Descriptions.Item label="置信度">
                {typeof detailRecord.confidence === 'number' ? `${Math.round(detailRecord.confidence * 100)}%` : '--'}
              </Descriptions.Item>
              <Descriptions.Item label="通用名称" span={2}>
                <Text copyable>{detailRecord.commonName || '--'}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="用户" span={2}>
                <div className="space-y-1 text-sm text-gray-600">
                  <div>Token：{detailRecord.userToken || '未留'}</div>
                  <div>IP：{detailRecord.userIp}</div>
                  <div>
                    App：{detailRecord.appInfo?.appVersion || '--'} · {detailRecord.appInfo?.osVersion || '--'}
                  </div>
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="Locale">{detailRecord.localizationCode || '--'}</Descriptions.Item>
              <Descriptions.Item label="创建时间">
                {dayjs(detailRecord.createdAt).format('YYYY-MM-DD HH:mm:ss')}
              </Descriptions.Item>
            </Descriptions>

            {detailRecord.thinking && (
              <div>
                <Text type="secondary">AI 思考</Text>
                <Paragraph copyable className="text-sm leading-6" style={{ whiteSpace: 'pre-wrap' }}>
                  {detailRecord.thinking}
                </Paragraph>
              </div>
            )}

            {detailRecord.description && (
              <div>
                <Text type="secondary">Description</Text>
                <Paragraph copyable className="text-sm leading-6" style={{ whiteSpace: 'pre-wrap' }}>
                  {detailRecord.description}
                </Paragraph>
              </div>
            )}

            {detailRecord.whereToFind && (
              <div>
                <Text type="secondary">Where to find</Text>
                <Paragraph copyable className="text-sm leading-6" style={{ whiteSpace: 'pre-wrap' }}>
                  {detailRecord.whereToFind}
                </Paragraph>
              </div>
            )}

            {detailRecord.funFact && (
              <div>
                <Text type="secondary">Fun Fact</Text>
                <Paragraph copyable className="text-sm leading-6" style={{ whiteSpace: 'pre-wrap' }}>
                  {detailRecord.funFact}
                </Paragraph>
              </div>
            )}

            <div>
              <Text type="secondary">Prompt</Text>
              <Paragraph copyable className="text-xs leading-6" style={{ whiteSpace: 'pre-wrap' }}>
                {detailRecord.prompt}
              </Paragraph>
            </div>

            {detailRecord.responseContent && (
              <div>
                <Text type="secondary">AI 响应</Text>
                <Paragraph copyable className="text-xs leading-6" style={{ whiteSpace: 'pre-wrap' }}>
                  {detailRecord.responseContent}
                </Paragraph>
              </div>
            )}
          </div>
        </Drawer>
      )}

      {textPreview && (
        <Modal open title={textPreview.title} footer={null} width={680} onCancel={() => setTextPreview(null)}>
          <Paragraph className="text-sm leading-6" copyable style={{ whiteSpace: 'pre-wrap' }}>
            {textPreview.content}
          </Paragraph>
        </Modal>
      )}
    </div>
  )
}
